import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Chip
} from '@mui/material';
import {
  Delete,
  CheckCircle,
  RecyclingOutlined,
  HomeWork,
  CleaningServices,
  LocalShipping,
  Euro
} from '@mui/icons-material';

function ClearanceDisposal() {
  const services = [
    {
      icon: <HomeWork sx={{ fontSize: 40, color: '#55d6dc' }} />,
      title: 'Haushaltsauflösungen',
      description: 'Komplette Räumung von Wohnungen und Häusern'
    },
    {
      icon: <Delete sx={{ fontSize: 40, color: '#55d6dc' }} />,
      title: 'Entrümpelung',
      description: 'Entsorgung von Sperrmüll und unbrauchbaren Gegenständen'
    },
    {
      icon: <RecyclingOutlined sx={{ fontSize: 40, color: '#55d6dc' }} />,
      title: 'Umweltgerechte Entsorgung',
      description: 'Fachgerechte Trennung und Recycling'
    },
    {
      icon: <CleaningServices sx={{ fontSize: 40, color: '#55d6dc' }} />,
      title: 'Besenreine Übergabe',
      description: 'Endreinigung nach der Entrümpelung'
    }
  ];

  const clearanceTypes = [
    'Wohnungsauflösung',
    'Haushaltsauflösung',
    'Kellerentrümpelung',
    'Dachbodenentrümpelung',
    'Garagenentrümpelung',
    'Messie-Wohnungen',
    'Nachlassauflösung',
    'Geschäftsauflösung'
  ];

  const disposalItems = [
    { item: 'Möbel', method: 'Sperrmüll / Recyclinghof' },
    { item: 'Elektrogeräte', method: 'Elektroschrott-Recycling' },
    { item: 'Matratzen', method: 'Sperrmüll' },
    { item: 'Teppiche', method: 'Restmüll / Sperrmüll' },
    { item: 'Farben/Lacke', method: 'Sondermüll' },
    { item: 'Batterien', method: 'Sammelstellen' },
    { item: 'Medikamente', method: 'Apotheke / Sondermüll' },
    { item: 'Kleidung', method: 'Altkleidercontainer / Spende' }
  ];

  const pricingFactors = [
    'Größe der Räumlichkeiten (m²)',
    'Menge des Entrümpelungsguts',
    'Zugänglichkeit (Etage, Aufzug)',
    'Art der zu entsorgenden Gegenstände',
    'Sondermüll oder Gefahrstoffe',
    'Gewünschte Zusatzleistungen'
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Delete sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Entrümpelung & Entsorgung
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Professionelle Räumung und umweltgerechte Entsorgung
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Wir räumen und entsorgen schnell, sauber und zuverlässig. Von der Kellerentrümpelung 
            bis zur kompletten Haushaltsauflösung – alles aus einer Hand.
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card sx={{
                bgcolor: '#1a3a44',
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                height: '100%',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ 
                      bgcolor: 'rgba(85,214,220,0.12)', 
                      borderRadius: '50%', 
                      p: 1.5,
                      display: 'flex'
                    }}>
                      {service.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'white', opacity: 0.85 }}>
                        {service.description}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Clearance Types */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Unsere Entrümpelungsleistungen
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {clearanceTypes.map((type) => (
              <Chip
                key={type}
                label={type}
                size="large"
                sx={{
                  bgcolor: '#1a3a44',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontSize: '1rem',
                  py: 3,
                  '&:hover': {
                    bgcolor: '#275664',
                    borderColor: '#55d6dc'
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Disposal Methods */}
        <Card sx={{ 
          bgcolor: 'rgba(255,255,255,0.05)', 
          backdropFilter: 'blur(10px)',
          mb: 6
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
              Fachgerechte Entsorgung
            </Typography>
            <Grid container spacing={2}>
              {disposalItems.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: '#1a3a44',
                      border: '1px solid rgba(255,255,255,0.08)'
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ color: 'white', fontWeight: 'bold' }}>
                        {item.item}
                      </Typography>
                      <Typography sx={{ color: '#55d6dc', fontSize: '0.9rem' }}>
                        {item.method}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Process */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Unser Ablauf
          </Typography>
          <Grid container spacing={3}>
            {[
              { step: '1', title: 'Besichtigung', desc: 'Kostenlose Vor-Ort-Besichtigung und Beratung' },
              { step: '2', title: 'Angebot', desc: 'Transparentes Festpreisangebot ohne versteckte Kosten' },
              { step: '3', title: 'Entrümpelung', desc: 'Professionelle Räumung zum vereinbarten Termin' },
              { step: '4', title: 'Übergabe', desc: 'Besenreine Übergabe der geräumten Räumlichkeiten' }
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#275664',
                    borderRadius: 2,
                    textAlign: 'center',
                    height: '100%'
                  }}
                >
                  <Box sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: '#55d6dc',
                    color: '#0a1f28',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    mx: 'auto',
                    mb: 2
                  }}>
                    {item.step}
                  </Box>
                  <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'white', opacity: 0.85 }}>
                    {item.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Pricing Info */}
        <Card sx={{ 
          bgcolor: '#1a3a44',
          mb: 6
        }}>
          <CardContent sx={{ p: 4 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Euro sx={{ color: '#ffd700', fontSize: 40 }} />
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                Preisgestaltung
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ color: 'white', mb: 3, opacity: 0.9 }}>
              Die Kosten für eine Entrümpelung hängen von verschiedenen Faktoren ab:
            </Typography>
            <List>
              {pricingFactors.map((factor, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: '#55d6dc' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={factor}
                    sx={{ color: 'white', opacity: 0.9 }}
                  />
                </ListItem>
              ))}
            </List>
            <Typography variant="body1" sx={{ color: '#55d6dc', mt: 2, fontWeight: 'bold' }}>
              Kostenlose Besichtigung und Festpreisangebot – keine versteckten Kosten!
            </Typography>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: '#275664', 
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <LocalShipping sx={{ fontSize: 60, color: '#ffd700', mb: 2 }} />
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Jetzt Entrümpelung beauftragen
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Schnell, sauber und zu fairen Preisen – wir räumen für Sie!
          </Typography>
          <Box
            component="button"
            onClick={() => window.location.href = '/contact'}
            style={{
              backgroundColor: '#55d6dc',
              color: '#275664',
              padding: '12px 32px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
          >
            Kostenloses Angebot anfordern
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default ClearanceDisposal;